import { useState, useRef, useEffect } from "react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Input } from "../ui/input";
import { toast } from "sonner";

// Move keyboard focus to the next focusable form element in document order.
// Used after the user commits a new supplier via Enter or Tab so they can keep typing
// the rest of the material row without a manual hop.
const focusNextFocusable = (currentEl) => {
  if (!currentEl) return;
  const focusable = Array.from(
    document.querySelectorAll(
      'input:not([disabled]):not([type="hidden"]), textarea:not([disabled]), select:not([disabled]), button:not([disabled]):not([tabindex="-1"]), [tabindex]:not([tabindex="-1"]):not([disabled])'
    )
  ).filter((el) => el.offsetParent !== null); // skip visually-hidden
  const idx = focusable.indexOf(currentEl);
  const next = focusable[idx + 1];
  if (next) next.focus();
};

/**
 * Inline supplier picker with "+ Add" → free-text fallback.
 *
 * Behaviour (per spec):
 * - Typed text commits on blur, Enter, OR Tab — never silently discarded.
 * - Empty / whitespace-only input on commit is a no-op (leaves value unchanged).
 * - Case-insensitive match against existing suppliers re-uses the existing one
 *   rather than creating a duplicate.
 * - A truly new supplier is added to Settings via `onAddSupplier(name)` AND set on
 *   the row via `onChange(name)`. A subtle toast confirms.
 * - Enter / Tab also move focus to the next focusable form element so the user
 *   can continue typing the material name without a manual click.
 *
 * Props:
 *   value            current supplier string ("" allowed → renders placeholder)
 *   suppliers        full list of known supplier names
 *   onChange(name)   called whenever the supplier on this row should change
 *   onAddSupplier(name)  async — persists a new name to Settings (parent handles)
 *   triggerClassName / triggerStyle — passthrough for size/style matching
 *   placeholder      empty-state placeholder text
 *   testId           data-testid root (e.g. "supplier-select-row-123")
 */
export function SupplierSelect({
  value,
  suppliers,
  onChange,
  onAddSupplier,
  triggerClassName = "h-8 rounded text-xs",
  triggerStyle,
  placeholder = "Supplier…",
  testId,
}) {
  const [adding, setAdding] = useState(false);
  const [typed, setTyped] = useState("");
  const inputRef = useRef(null);
  const committedRef = useRef(false); // guard against double-fire (Enter + blur)

  useEffect(() => {
    if (adding) {
      committedRef.current = false;
      setTyped("");
      // autoFocus prop on Input already gives focus; this is belt-and-braces.
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [adding]);

  const commit = async (afterCommitEl) => {
    if (committedRef.current) return;
    committedRef.current = true;
    const trimmed = (typed || "").trim();
    if (!trimmed) {
      // No-op per spec — pretend nothing happened.
      setAdding(false);
      return;
    }
    // Case-insensitive match → re-use existing.
    const existing = suppliers.find((s) => s.toLowerCase() === trimmed.toLowerCase());
    if (existing) {
      onChange(existing);
      setAdding(false);
      return;
    }
    // Truly new — add to settings AND set on row.
    try {
      await onAddSupplier(trimmed);
    } catch (e) {
      console.error("Failed to add supplier", e);
      // Even if the settings POST fails we still set the value on the row so the
      // user's typed text isn't lost. Next render they'll see it as an unknown
      // supplier (works because the Select falls back to the raw value).
    }
    onChange(trimmed);
    toast.success(`Supplier "${trimmed}" added.`);
    setAdding(false);
    // Focus next field so the user can keep typing.
    if (afterCommitEl) focusNextFocusable(afterCommitEl);
  };

  if (adding) {
    return (
      <Input
        ref={inputRef}
        autoFocus
        value={typed}
        onChange={(e) => setTyped(e.target.value)}
        onBlur={(e) => { commit(null); /* blur shouldn't yank focus */ }}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            commit(e.currentTarget);
          } else if (e.key === "Tab") {
            // Let Tab do its normal focus-move AFTER we commit. preventDefault would
            // trap focus on this input; instead we commit synchronously and let the
            // browser hand focus to the next element on its own.
            commit(null);
          } else if (e.key === "Escape") {
            committedRef.current = true; // skip the blur-commit that fires next tick
            setAdding(false);
            setTyped("");
          }
        }}
        placeholder="New supplier name…"
        className={triggerClassName}
        style={{ border: "1px solid var(--border)", ...(triggerStyle || {}) }}
        data-testid={testId ? `${testId}-input` : undefined}
      />
    );
  }

  return (
    <Select
      value={value || ""}
      onValueChange={(v) => {
        if (v === "__add__") {
          setAdding(true);
        } else {
          onChange(v);
        }
      }}
    >
      <SelectTrigger
        className={triggerClassName}
        style={{ border: "1px solid var(--border)", ...(triggerStyle || {}) }}
        data-testid={testId}
      >
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        {[...suppliers].sort((a, b) => a.localeCompare(b)).map((s) => (
          <SelectItem key={s} value={s}>{s}</SelectItem>
        ))}
        <SelectItem value="__add__">
          <span style={{ color: "var(--primary)" }}>+ Add</span>
        </SelectItem>
      </SelectContent>
    </Select>
  );
}

export default SupplierSelect;
